import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { matchRoutes, useLocation } from 'react-router-dom';
import { ENTRY_PATHS } from '../auth/entryPaths';
import { useSession } from '../auth/SessionProvider';
import { useLimits } from '../hooks/LimitsProvider';
import { useI18n } from '../i18n/I18nProvider';

/**
 * The viewer watermark (Q40, Q49, Q50): the viewer's name, the last digits of their number and the time,
 * tiled over every child's picture and every screen of a child's data in the guardian app, the staff app and
 * the admin console.
 *
 * A browser cannot block a screenshot, and nothing stops a photo of the screen taken with another phone. The
 * watermark does not prevent either: it puts in each of them whose screen it was and when. It is drawn on
 * screen only, and the stored files are unchanged (Q50), so developer tools remove it and a media link opened
 * directly shows the file without it. Nothing that describes it may claim more.
 *
 * The text is cut out of a repeating CSS mask over a block of the `watermark-ink` colour. A mask tiles to any
 * height, however long the page, and the colour stays one token, so dark mode is a token swap.
 */

/** The text's size in the tile, in CSS pixels. */
const FONT_PX = 14;

/** Roughly how wide one character is, as a share of the font size: generous, so that no text is clipped. */
const CHARACTER_WIDTH = 0.62;

/** The gap around the text in each tile, in CSS pixels, so that neighbouring tiles do not touch. */
const TILE_GAP_PX = 48;

/** How far the text is turned, in degrees: diagonal, so a crop along the edge of a picture cannot avoid it. */
const ANGLE_DEGREES = -30;

/** How often the time on it moves on. */
const REFRESH_MS = 60_000;

/** One tile of the mask: the text, turned, in an SVG image just large enough to hold it. */
export interface WatermarkTile {
  width: number;
  height: number;
  /** A `data:` URL of the SVG, for `mask-image`. */
  url: string;
}

const escapeXml = (text: string) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** The tile for `text`. Pure, so the sizes can be tested without a browser that draws. */
export function watermarkTile(text: string): WatermarkTile {
  const textWidth = [...text].length * FONT_PX * CHARACTER_WIDTH;
  const radians = (Math.abs(ANGLE_DEGREES) * Math.PI) / 180;
  const width = Math.ceil(textWidth * Math.cos(radians) + FONT_PX * Math.sin(radians)) + TILE_GAP_PX;
  const height = Math.ceil(textWidth * Math.sin(radians) + FONT_PX * Math.cos(radians)) + TILE_GAP_PX;
  const centre = `${width / 2} ${height / 2}`;

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">`
    + `<text x="50%" y="50%" text-anchor="middle" dominant-baseline="middle" font-family="system-ui, sans-serif" `
    + `font-size="${FONT_PX}" transform="rotate(${ANGLE_DEGREES} ${centre})">${escapeXml(text)}</text></svg>`;

  return { width, height, url: `url("data:image/svg+xml,${encodeURIComponent(svg)}")` };
}

/** The current minute, moving on by itself. */
function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), REFRESH_MS);
    return () => clearInterval(timer);
  }, []);
  return now;
}

/** What the watermark says for this viewer now, or null with no session. */
export function useWatermarkText(): string | null {
  const { t, formatDateTime } = useI18n();
  const { session } = useSession();
  const now = useNow();

  if (!session) return null;
  return t('watermark.text', {
    name: session.watermark.name,
    phone: session.watermark.phoneTail,
    time: formatDateTime(now),
  });
}

/**
 * The layer itself, filling its nearest positioned ancestor. Hidden from assistive technology, never the
 * target of a tap or a selection, and kept when the page is printed.
 */
export function Watermark() {
  const text = useWatermarkText();
  const { watermark } = useLimits();
  if (!text) return null;

  const tile = watermarkTile(text);
  const style: CSSProperties = {
    opacity: watermark.opacity,
    maskImage: tile.url,
    WebkitMaskImage: tile.url,
    maskSize: `${tile.width}px ${tile.height}px`,
    WebkitMaskSize: `${tile.width}px ${tile.height}px`,
  };

  return <div className="dg-watermark" aria-hidden="true" data-watermark={text} style={style} />;
}

/** Its children with the watermark over them. */
export function Watermarked({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={`dg-watermarked${className ? ` ${className}` : ''}`}>
      {children}
      <Watermark />
    </div>
  );
}

/** One route an app declares, as `App.tsx` writes it, and whether its screen shows a child's data. */
export interface RouteMark {
  /** The path as written in `App.tsx`; `index` for an index route. */
  route: string;
  /** The full path the screen answers on, for matching the location. */
  path: string;
  childData: boolean;
}

/**
 * The routes every tenant app declares before its signed-in shell, from `ENTRY_PATHS` (signing in, choosing a
 * membership, accepting an invitation, resetting a password, confirming a new number, each also under a
 * nursery's slug), and the shell itself. None shows a child.
 */
export const ENTRY_ROUTE_MARKS: readonly RouteMark[] = [
  ...ENTRY_PATHS.map((entry) => ({ route: entry.path, path: entry.path, childData: false })),
  { route: '/*', path: '/*', childData: false },
];

/** Whether the current location is one of `marks`' child-data screens. */
export function useChildDataRoute(marks: readonly RouteMark[]): boolean {
  const location = useLocation();
  // Ranked as the router ranks them, so '/children/new' is its own screen and not '/children/:id'.
  const matched = matchRoutes(marks.map((mark) => ({ path: mark.path, mark })), location);
  return matched?.at(-1)?.route.mark.childData ?? false;
}

/** Its children, under the watermark on a child-data screen and as they are on any other. */
export function WatermarkOnRoutes({ marks, children, className }: {
  marks: readonly RouteMark[];
  children: ReactNode;
  className?: string;
}) {
  return useChildDataRoute(marks)
    ? <Watermarked className={className}>{children}</Watermarked>
    : <>{children}</>;
}
